import { Box, Button, Typography } from '@mui/material';
import { useState } from 'react';
import HeiCheckbox from '../../../../../Common/components/Form/HeiCheckbox/HeiCheckbox';
import HeiDynamicField from '../../../../../Common/components/Form/HeiDynamicField/HeiDynamicField';
import HeiTextField from '../../../../../Common/components/Form/HeiTextField/HeiTextField';
import NavigationPrompt from '../../../../../Common/components/NavigationPrompt/NavigationPrompt';
import { mapCreateProjectFormField } from '../../../../../Common/utils/dynamicFieldUtil';
import ProjectTypeSelection from '../../../../CreateProject/components/ProjectTypeSelection/ProjectTypeSelection';
import { ProjectDetails } from '../../../api/models/ProjectDetails';
import PackageActionPopup from '../../../components/PackageActionPopup/PackageActionPopup';
import usePackageActions from '../../../hooks/usePackageActions';
import useUpdateProjectDetails from '../../../hooks/useUpdateProjectDetails';
import styles from './ProjectDetailsTab.module.scss';

import usePageTitle from '../../../../../Common/hooks/usePageTitle';
import ProjectTabSkeleton from '../../../components/ProjectTabSkeleton/ProjectTabSkeleton';
import { ProjectDetailsAlert } from './ProjectDetailsAlert';

interface ProjectDetailsTabProps {
    project: ProjectDetails;
    userCanEdit: boolean;
}

const ProjectDetailsTab = ({ project, userCanEdit }: ProjectDetailsTabProps) => {
    const { form, opcos, handleSubmit } = useUpdateProjectDetails(project);

    usePageTitle('Project | Details');

    const { packageCustomField, id, packageId, projectCustomFields } = project;
    const { packageActions, isLoading: isPackageActionsLoading } = usePackageActions(packageId);
    const dynamicFields = packageCustomField?.map((field) =>
        mapCreateProjectFormField(field, projectCustomFields),
    );

    const {
        control,
        watch,
        formState: { isDirty, isValid },
    } = form;

    const billingType = watch('billingType');
    const [isPackageActionPopupOpen, setIsPackageActionPopupOpen] = useState(false);
    const [actionIndex, setActionIndex] = useState(0);

    const handleActionClick = (index: number) => {
        setActionIndex(index);
        setIsPackageActionPopupOpen(true);
    };

    if (!opcos || isPackageActionsLoading) {
        return <ProjectTabSkeleton variant='details' />;
    }

    return (
        <Box sx={{ pl: '80px', pt: '24px', pb: 8, height: '100%' }}>
            <NavigationPrompt
                when={isDirty}
                title='Do you want to leave this page?'
                description='You have unsaved changes that will be lost if you decide to continue.'
            />
            <Typography variant={'h6'} sx={{ mb: '32px' }}>
                Project Details
            </Typography>

            <ProjectDetailsAlert project={project} billingType={billingType} />

            <form style={{ height: '100%' }} onSubmit={handleSubmit}>
                <HeiTextField
                    control={control}
                    name='projectName'
                    label='Project name'
                    sx={{ width: '45%' }}
                    disabled
                />
                <HeiCheckbox
                    control={control}
                    disabled={!userCanEdit}
                    name='hideProject'
                    label='Hide this project'
                    tooltip='Hide this project if it should not be publicly visible within Heineken. Note: team members cannot onboard themselves on hidden projects.'
                />
                {opcos && (
                    <ProjectTypeSelection
                        opcos={opcos}
                        form={form}
                        isEdit
                        canUserEdit={userCanEdit}
                    />
                )}
                {packageActions && packageActions.length > 0 && (
                    <PackageActionPopup
                        projectId={id}
                        packageAction={packageActions[actionIndex]}
                        open={isPackageActionPopupOpen}
                        handleClose={() => setIsPackageActionPopupOpen(false)}
                    />
                )}
                {packageActions?.map((action, index) => (
                    <p>
                        <Button
                            onClick={() => handleActionClick(index)}
                            variant='text'
                            disabled={project.flaggingStatus === 'Locked'}
                        >
                            {action.actionConfiguration.title}
                        </Button>
                    </p>
                ))}
                {dynamicFields && (
                    <>
                        <Typography
                            className={styles.projectDetails__packageDetailsTitle}
                            variant='h6'
                            sx={{ mb: '32px' }}
                        >
                            Package details
                        </Typography>

                        {dynamicFields.map((df) => (
                            <HeiDynamicField
                                key={df.name}
                                control={control}
                                field={{
                                    ...df,
                                    disabled: !userCanEdit || !df.isEditablePostCreation,
                                }}
                            />
                        ))}
                    </>
                )}

                {isDirty && (
                    <div className={styles.projectDetails__footer}>
                        <Button
                            variant='contained'
                            type='submit'
                            size='large'
                            sx={{ mr: 3 }}
                            disabled={!isValid}
                        >
                            Save
                        </Button>
                    </div>
                )}
            </form>
        </Box>
    );
};

export default ProjectDetailsTab;
